import { useCallback, useEffect, useState } from "react";
import { MdDelete, MdEdit, MdAdd } from "react-icons/md";
import { toast } from "react-toastify";
import AddProductModal from "./AddProductModal";
import { adminApiService } from "../../../services/api";
import EditProductModal from "./EditProductModal";

export default function ProductTable() {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editableProductId, setEditableProductId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [gridName, setGridName] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const toggleAddProduct = () => {
    setShowAddProduct(!showAddProduct);
    if (showAddProduct) {
      setEditableProductId(null);
    }
  };

  const fetchGrids = async () => {
    setIsLoading(true);
    try {
      const response = await adminApiService.getCustomProductGridAdmin();
      if (!response.ok) throw new Error("Failed to fetch product grids");
      const data = await response.json();
      setGridName(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching product grids");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGrids();
  }, []);

  const toggleEditProduct = () => {
    setShowEditProduct(!showEditProduct);
  };

  useEffect(() => {
    fetchProducts();
    fetchCategory();
  }, []);
  const fetchProductByCategory = useCallback(async () => {
    if (!selectedCategory) {
      fetchProducts();
      return;
    }
    try {
      const response = await adminApiService.getProductsByCategory(
        selectedCategory
      );

      if (!response.ok) throw new Error("Failed to fetch products.");
      const data = await response.json();

      setProducts(data.allProducts);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching products.");
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchProductByCategory();
  }, [selectedCategory, fetchProductByCategory]);

  const handleEditProductClick = (productId) => {
    setEditableProductId(productId);
    toggleEditProduct();
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const response = await adminApiService.deleteProduct(productId);
        if (!response.ok) throw new Error("Failed to delete the product.");
        toast.success("Product deleted successfully!");
        fetchProducts();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Error deleting the product.");
      }
    }
  };

  const handleToggleStatus = async (productId, field, value) => {
    try {
      const response = await adminApiService.updateProductStatus(productId, {
        [field]: !value,
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update the product.");
      toast.success("Product updated successfully!");
      fetchProductByCategory();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Error updating the product.");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await adminApiService.getAllProducts();

      if (!response.ok) throw new Error("Failed to fetch products.");
      const data = await response.json();
      setProducts(data.allProducts);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching products.");
    }
  };

  const fetchCategory = async () => {
    try {
      const response = await adminApiService.getAllCategories();
      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();

      setCategories(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown";
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="">
      {showAddProduct && (
        <AddProductModal
          toggleAddProduct={toggleAddProduct}
          showAddProduct={showAddProduct}
          onProductAdded={fetchProducts}
          productId={editableProductId}
        />
      )}

      {showEditProduct && (
        <EditProductModal
          toggleEditProduct={toggleEditProduct}
          showEditProduct={showEditProduct}
          onProductUpdated={fetchProducts}
          productId={editableProductId}
        />
      )}

      <div className="mt-10 rounded-lg bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <div className="relative flex items-center gap-5">
            <select
              id="category"
              className="block w-full rounded-md border border-gray-300 px-4 py-2.5 text-gray-700 shadow-sm transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.productCount})
                </option>
              ))}
            </select>
            <input
              id="search"
              type="text"
              placeholder="Search by product name..."
              className="block w-full rounded-md border border-gray-300 px-4 py-2.5 text-gray-700 shadow-sm transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="relative ">
            <button
              className="flex min-w-[150px] items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary/90"
              onClick={toggleAddProduct}
            >
              <MdAdd className="text-lg" />
              Add Product
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="whitespace-nowrap rounded-tl-lg px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Product Image
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Product Name
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Category
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Price
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Stock
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Status
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Featured
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Best Selling
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Active
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Coming Soon
                </th>
                {gridName.map((grid) => (
                  <th
                    key={grid.id}
                    className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700"
                  >
                    {grid.name}
                  </th>
                ))}
                <th className="whitespace-nowrap rounded-tr-lg px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map((product, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="h-12 w-12 overflow-hidden rounded-md border border-gray-200">
                      <img
                        src={adminApiService.getProductImageUrl(
                          product.product_images[0]
                        )}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">
                    {product.name}
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    {getCategoryName(product.category_id)}
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    <span className="font-medium text-gray-900">
                      {product.price_customer}
                    </span>{" "}
                    TK
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    {product.quantity}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                        product.quantity > 0
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {product.quantity > 0 ? "In Stock" : "Out of Stock"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={product.isfeatured}
                        onChange={() =>
                          handleToggleStatus(
                            product.id,
                            "isfeatured",
                            product.isfeatured
                          )
                        }
                      />
                      <div className="peer relative h-5 w-9 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-primary/30"></div>
                    </label>
                  </td>
                  <td className="px-6 py-4">
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={product.isbestselling}
                        onChange={() =>
                          handleToggleStatus(
                            product.id,
                            "isbestselling",
                            product.isbestselling
                          )
                        }
                      />
                      <div className="peer relative h-5 w-9 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-primary/30"></div>
                    </label>
                  </td>
                  <td className="px-6 py-4">
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={product.isactive}
                        onChange={() =>
                          handleToggleStatus(
                            product.id,
                            "isactive",
                            product.isactive
                          )
                        }
                      />
                      <div className="peer relative h-5 w-9 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-primary/30"></div>
                    </label>
                  </td>
                  <td className="px-6 py-4">
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={product.iscomingsoon}
                        onChange={() =>
                          handleToggleStatus(
                            product.id,
                            "iscomingsoon",
                            product.iscomingsoon
                          )
                        }
                      />
                      <div className="peer relative h-5 w-9 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-primary/30"></div>
                    </label>
                  </td>
                  {gridName.map((grid) => (
                    <td key={grid.id} className="px-6 py-4">
                      <label className="inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={product.iscustomdeal}
                          onChange={() =>
                            handleToggleStatus(
                              product.id,
                              "iscustomdeal",
                              product.iscustomdeal
                            )
                          }
                        />
                        <div className="peer relative h-5 w-9 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-primary/30"></div>
                      </label>
                    </td>
                  ))}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        aria-label="Edit Product"
                        className="rounded p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-primary"
                        onClick={() => handleEditProductClick(product.id)}
                      >
                        <MdEdit size={18} />
                      </button>
                      <button
                        aria-label="Delete Product"
                        className="rounded p-1.5 text-gray-500 transition hover:bg-red-50 hover:text-red-500"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredProducts.length === 0 && (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
